import { useEffect, useRef } from 'react'
import { getCable } from '@/lib/cable'

/** A change to one of an event's resources, as the server sends it (EventBroadcast, docs/REALTIME.md). */
export interface EventMessage<Data = unknown> {
  /** `<resource>.<change>`, such as `route.updated`. */
  type: string
  /** The resource as its REST endpoint returns it: the admin payload on `workspace`, the viewer payload on `public`. */
  data: Data
  /** When the server sent it (ISO 8601). */
  at: string
}

export type EventChannelScope = 'public' | 'workspace'

export type EventMessageHandler = (data: unknown, message: EventMessage) => void

const CHANNELS: Record<EventChannelScope, string> = {
  public: 'EventPageChannel',
  workspace: 'EventWorkspaceChannel',
}

/** What `lib/cable.ts` hands each handler when the server refuses the subscription. */
function isRejection(message: unknown): boolean {
  return typeof message === 'object' && message !== null && (message as { type?: unknown }).type === 'reject_subscription'
}

function isEventMessage(message: unknown): message is EventMessage {
  return typeof message === 'object' && message !== null && typeof (message as EventMessage).type === 'string' && 'data' in message
}

/**
 * An event's live changes over the app's one socket (`lib/cable.ts`): `workspace` for people who manage the event
 * (EventWorkspaceChannel, which takes the signed-in token), `public` for its viewers (EventPageChannel). `on` maps
 * message types to handlers, which merge `data` into what was loaded over REST. REST stays the source of truth:
 * `onResync` runs when the subscription is confirmed again after the socket dropped, so the caller loads again what
 * it may have missed. `onReject` runs when the server refuses the subscription (the account may no longer open the
 * workspace: a manager who was removed is refused when the socket comes back). Messages in the older shapes on the
 * public channel are left to their own hooks.
 */
export function useEventChannel(
  eventId: string | null | undefined,
  {
    scope,
    on,
    onResync,
    onReject,
  }: {
    scope: EventChannelScope
    on: Record<string, EventMessageHandler | undefined>
    onResync?: () => void
    onReject?: () => void
  },
) {
  // The latest handlers, so a re-render doesn't resubscribe.
  const handlers = useRef({ on, onResync, onReject })
  useEffect(() => {
    handlers.current = { on, onResync, onReject }
  })

  useEffect(() => {
    if (!eventId) return
    let confirmations = 0
    return getCable().subscribe(
      { channel: CHANNELS[scope], event_id: eventId },
      (message: unknown) => {
        if (isEventMessage(message)) handlers.current.on[message.type]?.(message.data, message)
        else if (isRejection(message)) handlers.current.onReject?.()
      },
      () => {
        confirmations += 1
        if (confirmations > 1) handlers.current.onResync?.()
      },
    )
  }, [eventId, scope])
}
